
import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /** Disables the add to cart button. */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /** Enables the add to cart button. */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return; // don't animate if exceeding max
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  /** Animates the fly to cart animation. */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /** Animates the add to cart button. */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Evita reazioni ai ripristini programmati */
  #isRestoringConfezione = false;

  /** Debounce id (compatibilità) */
  /** @type {number | undefined} */
  #checkDebounce;

  /** Valore selezionato dall’utente */
  #userSelectedConfezione = '';

  /** Diventa true dopo la prima interazione reale dell’utente */
  #hasInteractedConfezione = false;

  /** Congelamento label */
  /** @type {MutationObserver | null} */
  #labelFreezeObserver = null;
  /** @type {number | undefined} */
  #labelFreezeIdleTimeout;
  /** @type {number | undefined} */
  #labelFreezeHardStopTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = self.AbortSignal.timeout ? self.AbortSignal.timeout(30000) : this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#setupConfezioneValidation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#stopFreezeConfezioneLabel();
  }

  /** Setup validation for "Confezione" option */
  #setupConfezioneValidation() {
    const variantPicker = this.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker[data-force-confezione-selection="true"]');
    if (!variantPicker) return;

    const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
    if (!confezioneOption) return;

    const confezioneSelect = confezioneOption.querySelector('select');
    const confezioneInputs = confezioneOption.querySelectorAll('input[type="radio"]');

    if (confezioneSelect) {
      confezioneSelect.addEventListener('change', (e) => {
        if (this.#isRestoringConfezione) return;
        this.#hasInteractedConfezione = true; // prima interazione reale
        const newValue = /** @type {HTMLSelectElement} */ (e.target).value;
        this.#userSelectedConfezione = newValue;
        setTimeout(() => this.#checkConfezioneSelection(), 10);
      });
    }

    if (confezioneInputs.length > 0) {
      confezioneInputs.forEach((input) => {
        input.addEventListener('change', (e) => {
          if (this.#isRestoringConfezione) return;
          if (/** @type {HTMLInputElement} */ (e.target).checked) {
            this.#hasInteractedConfezione = true; // prima interazione reale
            const newValue = /** @type {HTMLInputElement} */ (e.target).value;
            this.#userSelectedConfezione = newValue;
            setTimeout(() => this.#checkConfezioneSelection(), 10);
          }
        });
      });
    }

    // Stato iniziale: mostra "Seleziona...", bottone coerente
    setTimeout(() => this.#checkConfezioneSelection(), 200);
  }

  /**
   * Aggiorna la label visiva del custom select (se presente)
   * @param {Element} confezioneOption
   * @param {string} value
   */
  #updateConfezioneVisual(confezioneOption, value) {
    const candidates = [
      '[data-selected-text]',
      '.selected-text',
      '.selected-value',
      '[aria-live][data-selected-text]',
    ];
    for (const sel of candidates) {
      const el = confezioneOption.querySelector(sel);
      if (el) el.textContent = value;
    }
  }

  /**
   * Rileva se una confezione è selezionata e ritorna il testo leggibile da mostrare.
   * Se è selezionato qualcosa → ritorna la label dell’opzione selezionata.
   * Se è tutto vuoto → ritorna null (così lasciamo “Seleziona…”).
   */
  #getDesiredConfezioneText() {
    const variantPicker = this.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker[data-force-confezione-selection="true"]');
    if (!variantPicker) return null;

    const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
    if (!confezioneOption) return null;

    const sel = confezioneOption.querySelector('select');
    const radios = confezioneOption.querySelectorAll('input[type="radio"]');

    // radio selezionato?
    for (const r of radios || []) {
      if (r.checked) {
        const lbl = confezioneOption.querySelector(`label[for="${r.id}"]`);
        return (lbl?.textContent?.trim() || r.value || '').trim() || null;
      }
    }

    // select con valore?
    if (sel && sel.value !== '') {
      const opt = sel.options[sel.selectedIndex];
      const txt = opt?.text?.trim();
      return (txt && txt.length) ? txt : sel.value || null;
    }

    // nulla selezionato → lascia placeholder
    return null;
  }

  /**
   * Applica la Confezione salvata direttamente al frammento "nuovo" PRIMA del morph,
   * ma solo dopo la prima interazione reale dell’utente.
   * @param {Document|Element} fragment
   * @returns {boolean} true se applicato
   */
  #applyConfezioneToFragment(fragment) {
    if (!this.#hasInteractedConfezione || !this.#userSelectedConfezione) return false;

    const newConfezioneOption = fragment.querySelector?.('[data-confezione-option="true"]');
    if (!newConfezioneOption) return false;

    const value = this.#userSelectedConfezione;
    let applied = false;

    // Prova select
    const newSelect = newConfezioneOption.querySelector('select');
    if (newSelect) {
      const optionExists = Array.from(newSelect.options).some((o) => o.value === value);
      if (optionExists) {
        newSelect.value = value; // silenzioso
        applied = true;
      }
    }

    // Altrimenti radio
    if (!applied) {
      const newRadios = newConfezioneOption.querySelectorAll('input[type="radio"]');
      if (newRadios?.length) {
        newRadios.forEach((r) => {
          r.checked = r.value === value;
        });
        applied = true;
      }
    }

    if (applied) {
      this.#updateConfezioneVisual(newConfezioneOption, value);
    }
    return applied;
  }

  /**
   * Congela la label e la mantiene coerente finché il DOM “rimbalza”.
   * Si ferma automaticamente quando non ci sono più mutazioni per 600ms (idle)
   * oppure dopo 3s (hard stop).
   */
  #startFreezeConfezioneLabel() {
    this.#stopFreezeConfezioneLabel(); // cleanup

    const variantPicker = this.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker[data-force-confezione-selection="true"]');
    if (!variantPicker) return;

    const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
    if (!confezioneOption) return;

    const getLabelNode = () =>
      confezioneOption.querySelector('[data-selected-text], .selected-text, .selected-value, [aria-live][data-selected-text]');

    const applyDesired = () => {
      const desired = this.#getDesiredConfezioneText();
      const node = getLabelNode();
      if (!node) return;

      if (desired) {
        if (node.textContent !== desired) node.textContent = desired;
      } else {
        // nessuna selezione → non forziamo nulla (lasciamo "Seleziona...")
      }
    };

    // applica subito
    applyDesired();

    const armIdle = () => {
      if (this.#labelFreezeIdleTimeout) clearTimeout(this.#labelFreezeIdleTimeout);
      this.#labelFreezeIdleTimeout = window.setTimeout(() => {
        this.#stopFreezeConfezioneLabel();
      }, 600);
    };

    // Hard stop in 3s
    this.#labelFreezeHardStopTimeout = window.setTimeout(() => {
      this.#stopFreezeConfezioneLabel();
    }, 3000);

    this.#labelFreezeObserver = new MutationObserver(() => {
      applyDesired();
      armIdle();
    });

    // osserva tutto il blocco confezione
    this.#labelFreezeObserver.observe(confezioneOption, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    armIdle();
  }

  /** Termina il freeze della label e pulisce l'observer */
  #stopFreezeConfezioneLabel() {
    if (this.#labelFreezeObserver) {
      this.#labelFreezeObserver.disconnect();
      this.#labelFreezeObserver = null;
    }
    if (this.#labelFreezeIdleTimeout) {
      clearTimeout(this.#labelFreezeIdleTimeout);
      this.#labelFreezeIdleTimeout = undefined;
    }
    if (this.#labelFreezeHardStopTimeout) {
      clearTimeout(this.#labelFreezeHardStopTimeout);
      this.#labelFreezeHardStopTimeout = undefined;
    }
  }

  /**
   * Controlla se la confezione è selezionata e abilita/disabilita il bottone (micro-task, prima del paint)
   */
  #checkConfezioneSelection() {
    queueMicrotask(() => {
      const variantPicker = this.closest('.shopify-section, dialog, product-card')
        ?.querySelector('variant-picker[data-force-confezione-selection="true"]');
      if (!variantPicker) return;

      const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
      if (!confezioneOption) return;

      let isSelected = false;
      const confezioneInputs = confezioneOption.querySelectorAll('input[type="radio"]');
      const confezioneSelect = confezioneOption.querySelector('select');

      if (confezioneInputs.length > 0) {
        confezioneInputs.forEach((input) => {
          if (input.checked) isSelected = true;
        });
      }

      if (confezioneSelect && confezioneSelect.value !== '') {
        isSelected = true;
      }

      if (isSelected) {
        this.refs.addToCartButtonContainer?.enable();
      } else {
        this.refs.addToCartButtonContainer?.disable();
      }
    });
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>}
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      this.#updateQuantityLabel(cartQty);
      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   * @param {Event} event
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Verifica selezione Confezione
    const variantPicker = this.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker[data-force-confezione-selection="true"]');
    if (variantPicker) {
      const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
      if (confezioneOption) {
        const confezioneInputs = confezioneOption.querySelectorAll('input[type="radio"]:checked');
        const confezioneSelect = confezioneOption.querySelector('select');

        const isConfezioneSelected =
          confezioneInputs.length > 0 || (confezioneSelect && confezioneSelect.value !== '');

        if (!isConfezioneSelected) {
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const errorMessage = 'Seleziona una confezione';

            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              const newTextNode = document.createTextNode(errorMessage);
              addToCartTextError.appendChild(newTextNode);
            }

            this.#setLiveRegionText(errorMessage);

            if (this.#timeout) clearTimeout(this.#timeout);
            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }
          return; // blocca submit
        }
      }
    }

    // Invia al carrello
    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const variantIdInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (!variantIdInput || !variantIdInput.value) {
      console.error('❌ Variant ID mancante al submit');
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const errorMessage = 'Errore: variante prodotto non trovata. Ricarica la pagina e riprova.';

        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }

        this.#setLiveRegionText(errorMessage);

        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }
      return;
    }

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }
          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );
          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);
            setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /** Aggiorna la label "in carrello" */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.removeAttribute('aria-live');
    liveRegion.textContent = '';
  }

  /**
   * Morphs or updates an element if newElement exists
   * @param {Element | null} currentElement
   * @param {Element | null} newElement
   * @param {Element | null} insertBefore
   */
  #morphOrUpdateElement(currentElement, newElement, insertBefore) {
    if (!currentElement && newElement && insertBefore) {
      insertBefore.insertAdjacentElement('beforebegin', newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (currentElement && newElement) {
      morph(currentElement, newElement);
    }
  }

  /**
   * Handles the variant update event.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.#userSelectedConfezione = ''; // reset salvato al cambio prodotto
      // Manteniamo #hasInteractedConfezione: scelta UX (puoi azzerarlo se vuoi).
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    // Congela la label SOLO dopo che l’utente ha interagito almeno una volta
    if (this.#hasInteractedConfezione) {
      this.#startFreezeConfezioneLabel();
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const fragment = event.detail.data.html;
    const newAddToCartButton = fragment.querySelector('[ref="addToCartButton"]');

    // Update the variant ID (o fallback)
    const newVariantId = event.detail.resource?.id ?? '';
    variantId.value = newVariantId;

    const variantPicker = this.closest('.shopify-section, dialog, product-card')
      ?.querySelector('variant-picker[data-force-confezione-selection="true"]');

    if (!newVariantId && variantPicker) {
      const firstVariantInput = fragment.querySelector('input[name="id"]');
      if (firstVariantInput && firstVariantInput.value) {
        variantId.value = firstVariantInput.value;
      }
    }

    // PRE-PATCH: solo dopo la prima interazione reale
    if (variantPicker && fragment) {
      const applied = this.#applyConfezioneToFragment(fragment);
      if (applied && event.detail.resource?.available !== false) {
        this.refs.addToCartButtonContainer?.enable();
      }
    }

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update button state (senza validazione Confezione)
    if (currentAddToCartButton) {
      if (!variantPicker) {
        if (event.detail.resource == null || event.detail.resource.available == false) {
          addToCartButtonContainer.disable();
        } else {
          addToCartButtonContainer.enable();
        }
      }
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Fly image
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Quantity constraints + rules
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      fragment.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = fragment.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = fragment.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          fragment.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          newQuantitySelector.setValue(currentQuantityValue);
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = fragment.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    await this.#fetchAndUpdateCartQuantity();

    // Ripristino "silenzioso" nel DOM vivo (utile se il picker resetta internamente)
    if (variantPicker && this.#userSelectedConfezione && this.#hasInteractedConfezione) {
      const confezioneOption = variantPicker.querySelector('[data-confezione-option="true"]');
      const confezioneSelect = confezioneOption?.querySelector('select');
      const confezioneInputs = confezioneOption?.querySelectorAll('input[type="radio"]');

      if (confezioneSelect && confezioneSelect.value !== this.#userSelectedConfezione) {
        const optionExists = Array.from(confezioneSelect.options).some(
          (opt) => opt.value === this.#userSelectedConfezione
        );
        if (optionExists) {
          this.#isRestoringConfezione = true;
          confezioneSelect.value = this.#userSelectedConfezione;
          this.#updateConfezioneVisual(confezioneOption, this.#userSelectedConfezione);
          this.#isRestoringConfezione = false;
        }
      }

      if (confezioneInputs && confezioneInputs.length > 0) {
        confezioneInputs.forEach((input) => {
          if (input.value === this.#userSelectedConfezione && !input.checked) {
            this.#isRestoringConfezione = true;
            input.checked = true;
            this.#updateConfezioneVisual(confezioneOption, this.#userSelectedConfezione);
            this.#isRestoringConfezione = false;
          }
        });
      }
    }

    // Ricontrolla stato (micro-task)
    this.#checkConfezioneSelection();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * (Intenzionalmente vuoto per evitare flash)
   */
  #onVariantSelected = () => {};
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;
  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = { x: rect.width / 2, y: rect.height / 2 };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    /** @type {number | null} */
    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    requestAnimationFrame(animate);
  }
}

/** Bézier helper */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
